import React, {createContext, Dispatch, Reducer, useCallback, useContext, useReducer} from 'react';
import {CustomerRole, IActivePage} from "../../pages/pages";
import { useHistory } from 'react-router-dom';
import { ElectricityAgreement } from 'models/electricity/agreement';
import { LOCAL_STORAGE_KEYS } from 'common/constants/localStorageKeys';
import { ChargingAgreement } from 'models/charging/agreement';

interface Actions {
    type: string | "setCustomerId" | "setCustomer" | "setAuthenticated" | 'setUser' | 'setUserId';
    value: any;
}

export interface IAuthProps {
    authenticated: boolean;
    customerId: number | null;
    customer: any | null;
    userId: any;
    user: any | null;
    content: JSX.Element | null;
    token: string | null;
    registerData: RegisterData | null;
    changePackageAgreement: ElectricityAgreement | null;
    viewOfferAgreement: ElectricityAgreement | null;
    viewChargingAgreementOffer: ChargingAgreement | null;
    showCardNotification: boolean;
}

export enum CustomerPermission {
    WEB_ACCESS,
    WEB_ADMIN,
    REPRESENTATIVE
}

export interface InitContextProps {
    state: IAuthProps;
    dispatch: Dispatch<Actions>;
    hasPermission: (role: CustomerPermission) => boolean;
}

interface AuthProviderProps {
    reducer: Reducer<IAuthProps, Actions>;
    initialState: IAuthProps;
    currentPage: IActivePage
}

export interface RegisterData {
    isCompany: boolean;
    companyId?: number;
    registryNumber?: string;
    vatRegistryNumber?: string;
    name?: string;
    customerId?: number;
}

export const AuthenticationContext = createContext({} as InitContextProps);

const RedirectPage = (props: any) => {
    let history = useHistory();
    setTimeout(() => {
        history.replace('/' + props.to);
        window.location.reload();
    }, 150);

    return <div></div>
};

export const AuthProvider: React.FC<AuthProviderProps> = ({reducer, initialState, children, currentPage}) => {
    const [state, dispatch] = useReducer(reducer, initialState);

    const hasPermission = useCallback((role: CustomerPermission): boolean => {
        const customer = state.customer;
        switch (role) {
            case CustomerPermission.REPRESENTATIVE:
                return !!customer?.representative;
            case CustomerPermission.WEB_ADMIN:
                return !!customer?.representative || !!customer?.webAdmin;
            case CustomerPermission.WEB_ACCESS:
                return !!customer?.representative || !!customer?.webAdmin || !!customer?.webAccess;
            default:
                return false;
        }
    }, [state.customer]);

    const value = { state, dispatch, hasPermission };

    let isAccessible;
    if (!currentPage.isPublic) {
        if (currentPage.requiredRoles) {
            if (state.authenticated && !state.customer) {
                isAccessible = true
            } else {
                isAccessible = isAccessibleForRoles(currentPage.requiredRoles, state.customer);
            }
            if (isAccessible && currentPage.onlyShowForRepresentative) {
                isAccessible = hasPermission(CustomerPermission.REPRESENTATIVE);
            }
        } else {
            isAccessible = true;
        }
    } else {
        isAccessible = true;
    }
    let result = children;
    if (!currentPage.isPublic) {
        if (isAccessible) {
            if (state.authenticated !== true ) {
                result = <RedirectPage to={"login"}/>
            }
        } else {
            if (state.authenticated === true ) {
                result = <RedirectPage to={"esileht"}/>
            } else {
                result = <RedirectPage to={"login"}/>
            }
        }
    }

    if (currentPage.onlyShowForPersons && state.customer && !state.customer.isPerson) {
        result = <RedirectPage to={"esileht"}/>;
    }
    if (currentPage.onlyShowForCompany && state.customer && state.customer.isPerson) {
        result = <RedirectPage to={"esileht"}/>;
    }
    if (currentPage.mustHaveGroupId && state.customer && !currentPage.mustHaveGroupId.includes(state.customer.groupId)) {
        result = <RedirectPage to={"esileht"}/>;
    }

    return (
        <AuthenticationContext.Provider value={value}>
            {
                result
            }
        </AuthenticationContext.Provider>
    );
};
export const useAuthContext: () => any = () => useContext(AuthenticationContext);

const AuthContext: React.FC<any> = ({children, currentPage}) => {
    const storedId = Number(localStorage.getItem(LOCAL_STORAGE_KEYS.CUSTOMER_ID));
    const initialState = storedId && !isNaN(storedId) ? storedId : null;
    const localToken = localStorage.getItem(LOCAL_STORAGE_KEYS.AUTHORIZATION_HEADER);

    const initState: IAuthProps = {
        authenticated: localToken !== null,
        customerId: initialState,
        customer: null,
        content: null,
        userId:  (localStorage.getItem(LOCAL_STORAGE_KEYS.USER_ID) && Number(localStorage.getItem(LOCAL_STORAGE_KEYS.USER_ID))) || null,
        user: null,
        token: localToken,
        registerData: null,
        changePackageAgreement: null,
        viewOfferAgreement: null,
        viewChargingAgreementOffer: null,
        showCardNotification: true
    };

    const reducer: Reducer<IAuthProps, Actions> = (state, action) => {
        switch (action.type) {
            case 'setAuthenticated':
                return {
                    ...state,
                    authenticated: action.value
                };
            case 'setCustomerId':
                return {
                    ...state,
                    customerId: action.value
                };
            case 'setCustomer':
                return {
                    ...state,
                    customer: action.value
                };
            case 'setUserId':
                return {
                    ...state,
                    userId: action.value
                };                 
            case 'setUser':
                return {
                    ...state,
                    user: action.value
                };                
            case 'setContent':
                return {
                    ...state,
                    content: action.value
                };
            case 'setToken':
                return {
                    ...state,
                    token: action.value
                };
            case 'setRegisterData':
                return {
                    ...state,
                    registerData: action.value
                };
            case 'setChangePackage':
                return {
                    ...state,
                    changePackageAgreement: action.value
                };
            case 'setViewOffer':
                return {
                    ...state,
                    viewOfferAgreement: action.value
                };
            case 'setViewChargingOffer':
                return {
                    ...state,
                    viewChargingAgreementOffer: action.value
                };
            case 'setShowCardNotification':
                return {
                    ...state,
                    showCardNotification: action.value
                };
    
            default:
                return state;
        }
    };

    if (localToken)
        initState.authenticated = true;

    return (
        <AuthProvider reducer={reducer} initialState={initState} currentPage={currentPage}>
            {children}
        </AuthProvider>
    );
};

export function isAccessibleForRoles(requiredRoles: any, customer: { roles: any[]; }) {
    if (requiredRoles === null) return true;
    if (customer === null) return false;

    let isAccessible = false;
    if (customer.roles === null || customer.roles === undefined) {
        return false;
    }
    for (const requiredRole of requiredRoles) {
        const foundRole = customer.roles.find((role: CustomerRole) => role === requiredRole);
        if (!isAccessible)
            isAccessible = foundRole !== undefined;
    }

    return isAccessible;
}
export default AuthContext;
